/**
 * Dev showcase for the analytics dot plot (`/dev/analytics-showcase`).
 *
 * Renders `DotPlotGrid` against a deterministic fixture so the custom grid —
 * weekend shading, category-colored dots, size buckets, onboarding rings,
 * sticky user column, and the shared tooltip — can be validated without the
 * live `{env}-optr-analytics` backend. The summary breakdown charts reuse the
 * already-validated `<Chart>` component (see `/dev/chart-showcase`).
 */

import { useState } from 'react';
import { DotPlotGrid } from '@/components/analytics/DotPlotGrid';
import { DotPlotSkeleton } from '@/components/analytics/DotPlotSkeleton';
import { UserDotPlot } from '@/components/analytics/UserDotPlot';
import { categoryColor, userDisplayName } from '@/lib/analytics/dotplot';
import { useTheme } from '@/store/theme-context';
import type {
  CatalogResponse,
  DotplotUser,
  DotplotCell,
} from '@/types/analytics';

const CATALOG: CatalogResponse = {
  schemaVersion: 1,
  categories: [
    { key: 'runs', label: 'Runs', events: ['run.started', 'run.completed'], color: { light: '#2a78d6', dark: '#3987e5' } },
    { key: 'chat', label: 'Chat', events: ['message.added'], color: { light: '#1baf7a', dark: '#199e70' } },
    { key: 'tools', label: 'Tools', events: ['tool.executed'], color: { light: '#eda100', dark: '#c98500' } },
    { key: 'builder', label: 'Builder', events: ['agent.created'], color: { light: '#008300', dark: '#008300' } },
    { key: 'knowledge', label: 'Knowledge', events: ['kb.searched'], color: { light: '#4a3aa7', dark: '#9085e9' } },
    { key: 'setup', label: 'Configuration', events: ['space.created'], color: { light: '#e34948', dark: '#e66767' } },
    { key: 'inbox', label: 'Inbox', events: ['inbox.item_created'], color: { light: '#e87ba4', dark: '#d55181' } },
  ],
  markers: { firstSeen: 'user.first_seen' },
};

const CAT_KEYS = CATALOG.categories.map((c) => c.key);
const EVENTS_BY_CAT: Record<string, string[]> = Object.fromEntries(
  CATALOG.categories.map((c) => [c.key, c.events]),
);

// Deterministic LCG so the fixture is stable across reloads (no Math.random).
function makeRng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

function buildFixture(userCount: number, dayCount: number) {
  const days: string[] = [];
  const start = Date.UTC(2026, 5, 14); // 2026-06-14
  for (let i = 0; i < dayCount; i++) {
    days.push(new Date(start + i * 86400_000).toISOString().slice(0, 10));
  }

  const names = [
    ['Ada', 'Lovelace'], ['Grace', 'Hopper'], ['Alan', 'Turing'],
    ['Katherine', 'Johnson'], ['Edsger', 'Dijkstra'], ['Barbara', 'Liskov'],
    ['Donald', 'Knuth'], ['Margaret', 'Hamilton'], ['Tim', 'Berners-Lee'],
    ['Radia', 'Perlman'], ['Ken', 'Thompson'], ['Frances', 'Allen'],
  ];

  const rng = makeRng(42);
  const AUTOMATION_KEY = 'message.added:automation';
  const users: DotplotUser[] = [];
  for (let u = 0; u < userCount; u++) {
    const [firstName, lastName] = names[u % names.length];
    const onboardIdx = Math.floor(rng() * (dayCount * 0.6));
    const cells: Record<string, DotplotCell> = {};
    for (let d = onboardIdx; d < dayCount; d++) {
      const hasHuman = rng() >= 0.35; // ~65% human-active days
      const hasAutomation = rng() < 0.45; // background/machine volume
      if (!hasHuman && !hasAutomation) continue; // truly empty day

      const byCategory: Record<string, number> = {};
      const byEvent: Record<string, number> = {};
      let total = 0;
      if (hasHuman) {
        const catCount = 1 + Math.floor(rng() * 3);
        for (let c = 0; c < catCount; c++) {
          const key = CAT_KEYS[Math.floor(rng() * CAT_KEYS.length)];
          const n = 1 + Math.floor(rng() * 25);
          byCategory[key] = (byCategory[key] ?? 0) + n;
          const ev = EVENTS_BY_CAT[key][0];
          byEvent[ev] = (byEvent[ev] ?? 0) + n;
          total += n;
        }
        if (rng() < 0.15) total += 45; // occasional heavy day → big dot
      }

      // Automation is kept out of total/byCategory; detail under byEvent.
      let automation = 0;
      if (hasAutomation) {
        automation = 1 + Math.floor(rng() * 30);
        byEvent[AUTOMATION_KEY] = automation;
      }

      cells[days[d]] = {
        total,
        byCategory,
        byEvent,
        firstSeen: d === onboardIdx,
        ...(automation > 0 ? { automation } : {}),
      };
    }
    users.push({
      userId: `usr-fixture${u.toString().padStart(4, '0')}`,
      firstName,
      lastName,
      email: `${firstName.toLowerCase()}@example.com`,
      firstActiveDay: days[onboardIdx],
      cells,
    });
  }
  return { days, users };
}

const FIXTURE = buildFixture(12, 30);

export default function AnalyticsShowcasePage() {
  const { resolvedTheme } = useTheme();
  const [focusId, setFocusId] = useState<string | null>(null);
  const focusUser = focusId
    ? FIXTURE.users.find((u) => u.userId === focusId)
    : undefined;
  return (
    <div className="min-h-screen space-y-6 bg-background p-8 text-foreground">
      <div>
        <h1 className="text-2xl font-bold">Analytics dot plot — dev showcase</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Fixture data. 12 users × 30 days, one grid per activity type. Onboarding
          days show a tinted cell. Hover or Tab to a dot for the tooltip.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
        {CATALOG.categories.map((c) => (
          <span key={c.key} className="inline-flex items-center gap-1.5">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ background: categoryColor(c, resolvedTheme) }}
            />
            {c.label}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3.5 rounded-sm bg-amber-400/30" />
          Onboarding day (tinted cell)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full border-[1.5px] border-muted-foreground" />
          Automation only
        </span>
      </div>

      {focusUser ? (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setFocusId(null)}
            className="text-sm text-primary hover:underline"
          >
            ← All users
          </button>
          <h2 className="text-lg font-semibold">{userDisplayName(focusUser)}</h2>
          <UserDotPlot user={focusUser} days={FIXTURE.days} catalog={CATALOG} />
        </div>
      ) : (
        <DotPlotGrid
          days={FIXTURE.days}
          users={FIXTURE.users}
          catalog={CATALOG}
          onSelectUser={setFocusId}
        />
      )}

      <div className="border-t border-border pt-6">
        <h2 className="mb-3 text-lg font-semibold">Loading state</h2>
        <DotPlotSkeleton />
      </div>
    </div>
  );
}
